import React, { useState, useEffect, useCallback } from 'react';
import {
  Box, Typography, IconButton, Chip, Skeleton, Tooltip,
} from '@mui/material';
import BookmarkBorderIcon from '@mui/icons-material/BookmarkBorder';
import BookmarkRemoveIcon from '@mui/icons-material/BookmarkRemove';
import CheckIcon from '@mui/icons-material/Check';
import AddIcon from '@mui/icons-material/Add';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import LocationOnOutlinedIcon from '@mui/icons-material/LocationOnOutlined';
import ExploreOutlinedIcon from '@mui/icons-material/ExploreOutlined';
import SavedItemsService from '../../../services/savedItemsService';

const LABELS = {
  th: {
    empty: 'ยังไม่มีรายการที่บันทึกไว้',
    emptySub: 'กดปุ่มบันทึก เพื่อเก็บสถานที่หรือกิจกรรมที่สนใจไว้ที่นี่',
    explore: 'สำรวจ',
    goingLabel: 'จะไปแล้ว',
    notGoingLabel: 'จะไป?',
    goingHint: 'กำลังจะไป — คลิกเพื่อยกเลิก',
    notGoingHint: 'คลิกเพื่อตั้งเป็นจะไป',
    remove: 'ยกเลิกการบันทึก',
    loading: 'กำลังโหลด...',
    typeEvent: 'กิจกรรม',
    typeReview: 'รีวิว',
    typeVenue: 'สถานที่',
  },
  en: {
    empty: 'No saved items yet',
    emptySub: 'Tap the bookmark button on any event, review or venue to save it here.',
    explore: 'Explore',
    goingLabel: 'Going',
    notGoingLabel: 'Going?',
    goingHint: 'You\'re going — click to cancel',
    notGoingHint: 'Click to mark as going',
    remove: 'Remove',
    loading: 'Loading...',
    typeEvent: 'Event',
    typeReview: 'Review',
    typeVenue: 'Venue',
  },
  zh: {
    empty: '还没有已保存的项目',
    emptySub: '点击书签按钮保存感兴趣的活动或地点。',
    explore: '探索',
    goingLabel: '将去',
    notGoingLabel: '去吗?',
    goingHint: '您将去 — 点击取消',
    notGoingHint: '点击标记为将去',
    remove: '移除',
    loading: '加载中...',
    typeEvent: '活动',
    typeReview: '评论',
    typeVenue: '地点',
  },
};

const TYPE_COLOR = { event: '#7c3aed', review: '#0ea5e9', venue: '#10b981' };

const formatDate = (dateStr) => {
  if (!dateStr) return '';
  try { return new Date(dateStr).toLocaleDateString(); } catch { return dateStr; }
};

const SavedItemsList = ({ navigate, language = 'th', refreshKey }) => {
  const langKey = language?.toLowerCase() === 'cn' ? 'zh' : (language?.toLowerCase() || 'en');
  const t = LABELS[langKey] || LABELS.en;

  const [items, setItems] = useState([]);
  const [loadingInit, setLoadingInit] = useState(true);
  const [loadingIds, setLoadingIds] = useState(new Set());

  const getAuthHeaders = useCallback(() => {
    const token = localStorage.getItem('token');
    return token ? { headers: { Authorization: `Bearer ${token}` } } : {};
  }, []);

  useEffect(() => {
    setLoadingInit(true);
    SavedItemsService.getUserSavedItems(getAuthHeaders())
      .then((data) => setItems(data.items || []))
      .catch((err) => console.error('[SavedItemsList] fetch error:', err))
      .finally(() => setLoadingInit(false));
  }, [getAuthHeaders, refreshKey]);

  const setLoading = (id, on) =>
    setLoadingIds((prev) => { const s = new Set(prev); on ? s.add(id) : s.delete(id); return s; });

  const handleToggleGoing = async (e, item) => {
    e.stopPropagation();
    const key = `${item.item_type}-${item.item_id}`;
    if (loadingIds.has(key)) return;
    setLoading(key, true);
    try {
      const result = await SavedItemsService.toggleGoing(item.item_id, item.item_type, getAuthHeaders());
      setItems((prev) => {
        const updated = prev.map((i) =>
          i.item_id === item.item_id && i.item_type === item.item_type
            ? { ...i, is_going: result.is_going } : i
        );
        return [...updated.filter((i) => i.is_going), ...updated.filter((i) => !i.is_going)];
      });
    } catch (err) {
      console.error('[SavedItemsList] toggle going error:', err);
    } finally {
      setLoading(key, false);
    }
  };

  const handleRemove = async (e, item) => {
    e.stopPropagation();
    const key = `${item.item_type}-${item.item_id}`;
    if (loadingIds.has(key)) return;
    setLoading(key, true);
    try {
      await SavedItemsService.toggleSave(item.item_id, item.item_type, {}, getAuthHeaders());
      setItems((prev) =>
        prev.filter((i) => !(i.item_id === item.item_id && i.item_type === item.item_type))
      );
    } catch (err) {
      console.error('[SavedItemsList] remove error:', err);
    } finally {
      setLoading(key, false);
    }
  };

  const getTypeLabel = (type) => t[`type${type.charAt(0).toUpperCase()}${type.slice(1)}`] || type;

  // ── Loading skeletons ──
  if (loadingInit) {
    return (
      <Box sx={{ p: 2, display: 'flex', flexDirection: 'column', gap: 1.5 }}>
        {[1, 2, 3].map((n) => (
          <Box key={n} sx={{ bgcolor: '#fff', borderRadius: 2, p: 2, boxShadow: '0 1px 4px rgba(0,0,0,0.06)' }}>
            <Skeleton variant="text" width="60%" height={22} />
            <Skeleton variant="text" width="35%" height={16} sx={{ mt: 0.5 }} />
            <Skeleton variant="text" width="45%" height={14} sx={{ mt: 0.5 }} />
          </Box>
        ))}
      </Box>
    );
  }

  // ── Empty state ──
  if (items.length === 0) {
    return (
      <Box sx={{
        display: 'flex', flexDirection: 'column', alignItems: 'center',
        justifyContent: 'center', minHeight: 320, px: 3, py: 5, bgcolor: '#fff',
      }}>
        <Box sx={{
          width: 72, height: 72, borderRadius: '50%', bgcolor: '#f5f3ff',
          display: 'flex', alignItems: 'center', justifyContent: 'center', mb: 2.5,
        }}>
          <BookmarkBorderIcon sx={{ fontSize: 34, color: '#7c3aed' }} />
        </Box>
        <Typography variant="subtitle1" sx={{ fontWeight: 700, color: '#111827', mb: 0.75, textAlign: 'center' }}>
          {t.empty}
        </Typography>
        <Typography variant="body2" sx={{ color: '#9ca3af', textAlign: 'center', maxWidth: 240, lineHeight: 1.6, mb: 3 }}>
          {t.emptySub}
        </Typography>
        <Box
          onClick={() => navigate && navigate('/')}
          sx={{
            display: 'flex', alignItems: 'center', gap: 0.75,
            px: 2.5, py: 1, borderRadius: 999, bgcolor: '#7c3aed', color: '#fff',
            cursor: 'pointer', fontSize: '0.85rem', fontWeight: 600,
            '&:hover': { bgcolor: '#6d28d9' },
          }}
        >
          <ExploreOutlinedIcon sx={{ fontSize: 16 }} />
          {t.explore}
        </Box>
      </Box>
    );
  }

  // ── Item list ──
  return (
    <Box sx={{ bgcolor: '#f9fafb', minHeight: '100%', p: 1.5, display: 'flex', flexDirection: 'column', gap: 1.25 }}>
      {items.map((item) => {
        const key = `${item.item_type}-${item.item_id}`;
        const busy = loadingIds.has(key);
        const accent = TYPE_COLOR[item.item_type] || '#7c3aed';
        const isEvent = item.item_type === 'event';

        return (
          <Box
            key={key}
            onClick={() => { if (isEvent) window.location.href = `/event/${item.item_id}`; }}
            sx={{
              bgcolor: '#fff',
              borderRadius: 2.5,
              boxShadow: '0 1px 4px rgba(0,0,0,0.07)',
              overflow: 'hidden',
              display: 'flex',
              borderLeft: item.is_going ? '3px solid #7c3aed' : '3px solid transparent',
              cursor: isEvent ? 'pointer' : 'default',
              opacity: busy ? 0.7 : 1,
              transition: 'box-shadow 0.15s, opacity 0.15s',
              '&:hover': isEvent ? { boxShadow: '0 3px 10px rgba(0,0,0,0.11)' } : {},
            }}
          >
            {/* Thumbnail */}
            {item.item_image_url ? (
              <Box component="img" src={item.item_image_url} alt={item.item_name}
                sx={{ width: 70, minWidth: 70, objectFit: 'cover', bgcolor: '#f3f4f6' }} />
            ) : (
              <Box sx={{
                width: 70, minWidth: 70, bgcolor: '#f5f3ff',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
              }}>
                <BookmarkBorderIcon sx={{ color: accent, fontSize: 22, opacity: 0.5 }} />
              </Box>
            )}

            {/* Content */}
            <Box sx={{ flex: 1, px: 1.5, py: 1.25, minWidth: 0 }}>
              {/* Title row */}
              <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 0.75, mb: 0.5 }}>
                <Typography variant="body2"
                  sx={{ fontWeight: 700, color: '#111827', lineHeight: 1.3, flex: 1, minWidth: 0 }}
                  noWrap title={item.item_name}>
                  {item.item_name || '—'}
                </Typography>
                <Chip label={getTypeLabel(item.item_type)} size="small" sx={{
                  height: 18, fontSize: '0.62rem', fontWeight: 600,
                  bgcolor: `${accent}18`, color: accent, flexShrink: 0,
                }} />
              </Box>

              {/* Meta row */}
              {item.item_date && (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 0.25 }}>
                  <CalendarTodayIcon sx={{ fontSize: 11, color: '#9ca3af' }} />
                  <Typography variant="caption" sx={{ color: '#9ca3af', fontSize: '0.7rem' }}>
                    {formatDate(item.item_date)}
                  </Typography>
                </Box>
              )}
              {item.item_location && (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                  <LocationOnOutlinedIcon sx={{ fontSize: 11, color: '#9ca3af' }} />
                  <Typography variant="caption" sx={{
                    color: '#9ca3af', fontSize: '0.7rem',
                    overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                  }}>
                    {item.item_location}
                  </Typography>
                </Box>
              )}

              {/* Going toggle chip — lives inside content so it doesn't break card layout */}
              <Box sx={{ mt: 1 }} onClick={(e) => e.stopPropagation()}>
                <Tooltip title={item.is_going ? t.goingHint : t.notGoingHint} placement="bottom-start">
                  <Chip
                    size="small"
                    icon={item.is_going
                      ? <CheckIcon sx={{ fontSize: '13px !important', color: item.is_going ? '#fff !important' : undefined }} />
                      : <AddIcon sx={{ fontSize: '13px !important' }} />}
                    label={item.is_going ? t.goingLabel : t.notGoingLabel}
                    disabled={busy}
                    onClick={(e) => handleToggleGoing(e, item)}
                    sx={{
                      height: 22,
                      fontSize: '0.68rem',
                      fontWeight: 600,
                      cursor: 'pointer',
                      userSelect: 'none',
                      transition: 'all 0.18s',
                      ...(item.is_going ? {
                        bgcolor: '#7c3aed',
                        color: '#fff',
                        '& .MuiChip-icon': { color: '#fff' },
                        '&:hover': { bgcolor: '#b91c1c', '& .MuiChip-label': { textDecoration: 'line-through' } },
                      } : {
                        bgcolor: 'transparent',
                        color: '#6b7280',
                        border: '1px solid #e5e7eb',
                        '& .MuiChip-icon': { color: '#9ca3af' },
                        '&:hover': { bgcolor: '#f5f3ff', borderColor: '#7c3aed', color: '#7c3aed', '& .MuiChip-icon': { color: '#7c3aed' } },
                      }),
                    }}
                  />
                </Tooltip>
              </Box>
            </Box>

            {/* Remove button */}
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', pr: 1.25 }}>
              <Tooltip title={t.remove}>
                <IconButton size="small" disabled={busy}
                  onClick={(e) => handleRemove(e, item)}
                  sx={{ p: 0.6, color: '#d1d5db', '&:hover': { color: '#ef4444', bgcolor: '#fff5f5' } }}>
                  <BookmarkRemoveIcon sx={{ fontSize: 18 }} />
                </IconButton>
              </Tooltip>
            </Box>
          </Box>
        );
      })}
    </Box>
  );
};

export default SavedItemsList;
